import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

const representativeFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .transform((val) => val.trim()),
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
  phone: z
    .string()
    .optional()
    .transform((val) => val?.trim()),
  signatory: z.boolean().optional(),
});

export type IRepresentativeForm = z.infer<typeof representativeFormSchema>;

type Props = {
  onSubmit: (values: IRepresentativeForm) => void;
  onCancel: () => void;
  initialValues?: IRepresentativeForm;
  withSignatory?: boolean;
};

const FormContent = ({
  form,
  withSignatory = true,
}: {
  form: UseFormReturn<IRepresentativeForm>;
  withSignatory?: boolean;
}) => (
  <div className="space-y-4 pb-6">
    <FormField
      control={form.control}
      name="name"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Name</FormLabel>
          <FormControl>
            <Input {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />

    <FormField
      control={form.control}
      name="email"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Email</FormLabel>
          <FormControl>
            <Input {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />

    <FormField
      control={form.control}
      name="phone"
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            Phone Number <i className="font-normal ml-1">(optional)</i>
          </FormLabel>
          <FormControl>
            <Input {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />

    {withSignatory && (
      <FormField
        control={form.control}
        name="signatory"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-start gap-2">
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <div className="text-sm space-y-1.5">
                <div className="text-foreground font-medium">Signatory</div>
                <div className="text-muted-foreground">
                  Send an e-signature request to this contact to sign the approved escrow agreement.
                </div>
              </div>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    )}
  </div>
);

const RepresentativeForm = ({ onSubmit, onCancel, initialValues, withSignatory = true }: Props) => {
  const form = useForm<IRepresentativeForm>({
    resolver: zodResolver(representativeFormSchema),
    defaultValues: initialValues,
    mode: 'onChange',
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormContent form={form} withSignatory={withSignatory} />

        <div className="flex justify-between gap-4">
          <Button variant="outline" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting || !form.formState.isValid}
          >
            Save
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default RepresentativeForm;
